function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase(),
  );
}

/**
 * Utility function for checking if the given string is empty (null or undefined)
 * @param value string to check
 * @return boolean stating whether or not the given input is empty
 */
const isEmpty = (value: string | null | undefined): boolean => (value === null || value === undefined);

/**
 * Utility function for checking if the given string is blank
 * @param value string to check
 * @return boolean stating whether or not the given input is blank
 */
const isBlank = (value: string | null | undefined): boolean => (isEmpty(value) || value?.trim() === '');

/**
 * Safely extracts everything before the first '(' character.
 * @param input - The input string, or null/undefined.
 * @returns The trimmed substring before the first '(', or an empty string if input is nullish.
 */
function getBeforeFirstParen(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';

  const index = input.indexOf('(');
  return index === -1 ? input.trim() : input.slice(0, index).trim();
}

/**
 * Returns string length, counting newline ("\n") as 2 characters.
 * Null/undefined-safe.
 * @param {string|null|undefined} str
 * @returns {number}
 */
function lengthWithDoubleNewlines(str: string | null | undefined): number {
  return [...(str ?? '')].reduce(
    (count, ch) => count + (ch === '\n' ? 2 : 1),
    0,
  );
}

/**
 * Take any string and remove the single and double quote characters. Useful for avoiding issues with the
 * Sqlite parser.
 * @param value - string to be stripped of quotes
 */
const encodeSpecialChars = (value: string) => value
  .replaceAll('"', '%22')
  .replaceAll("'", '%27')
  .replaceAll('“', '%E2%80%9C')
  .replaceAll('‘', '%E2%80%98')
  .replaceAll('(', '%28')
  .replaceAll(')', '%29')
  .replaceAll('[', '%5B')
  .replaceAll(']', '%5D');

export {
  toTitleCase, isEmpty, isBlank, encodeSpecialChars, getBeforeFirstParen, lengthWithDoubleNewlines,
};
